import axios from 'axios';
import { navigate } from 'gatsby';
import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-use';

import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import ComponentButton from 'components/Button/ComponentButton';
import { HubspotFormFields, PrivacyPolicy, Submit } from 'components/Form/HubspotForms/HubspotFormFields';
import useHubspotFormData from 'components/Form/HubspotForms/useHubspotFormData';
import {
  findField,
  getInitValues,
  getUserHutk,
  handleDriftFastlane,
  isFieldVisible,
  prepareFormSubmissionValues,
  setGTMData,
} from 'components/Form/HubspotForms/utils';

import { enableHoneypot } from 'utils/enableHoneypot';
import { validateEmail } from 'utils/functions';
import parseUrl from 'utils/parseUrl';
import richTextParser from 'utils/richTextParser';

import type { ContentfulAsset, ContentfulComponentForm } from 'graphqlTypes';

interface FormProps
  extends FlexProps,
    Omit<ContentfulComponentForm, 'id' | 'children' | 'contentful_id' | 'internal' | 'node_locale'> {
  cid?: string;
  assetData?: ContentfulAsset;
  isDownloadPDF?: boolean;
  closeModal?: any;
  onSuccess?: (data: any) => void;
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hbspt: any;
  }
}

const HubspotForm: FC<FormProps> = ({
  heading,
  body,
  cid = 'unknowncid',
  assetData,
  isDownloadPDF,
  closeModal,
  hubspotForm,
  driftFastlaneCampaignId,
  onSuccess,
  buttonLabel,
}) => {
  const location = useLocation();
  const hsId = hubspotForm?.guid;
  const assetUrl = assetData?.file?.url;
  const hubspotFormData = useHubspotFormData();
  const [formSubmission, setFormSubmission] = useState(false);
  const [pdfSubmitted, setPDFSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formRef = useRef<HTMLDivElement>(null);
  const form = hubspotFormData.find(x => x.guid === hsId);
  const [ipAddress, setIpAddress] = useState(null);
  const [values, setValues] = useState({});
  const [validation, setValidation] = useState({});
  const [hasValidatedOnce, setValidatedOnce] = useState<boolean>(false);
  const [fastlaneId, setFastlaneId] = useState(driftFastlaneCampaignId);

  const privacyPolicyHTML = form
    ? JSON.parse(form.metaData.find(x => x.name === 'legalConsentOptions')?.value || '{}')?.privacyPolicyText
    : '';

  // remove useEffect when completely sunsetting CP.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fastlaneID = params.get('fastlaneID');
    if (fastlaneID) {
      setFastlaneId(parseInt(fastlaneID));
    }
  }, [location]);

  useEffect(() => {
    if (!form) {
      return;
    }

    (async () => {
      const initValues = { ...getInitValues(form), cid };
      try {
        const hutk = getUserHutk();
        const valuesToSet = initValues;
        if (hutk) {
          const res = await axios.get(`${process.env.GATSBY_API_URL}/hubspot/aspire/contact/utk/${hutk}/profile`);
          const userProps = res?.data?.properties;

          if (userProps) {
            Object.keys(initValues).forEach((k: string) => {
              if (userProps[k]?.value && isFieldVisible(findField(form, k), initValues)) {
                valuesToSet[k] = userProps[k].value;
              }
            });
          }
        }
        setValues(valuesToSet);
      } catch (err) {
        console.log(err);
        setValues(initValues);
      }
    })();
  }, []);

  useEffect(() => {
    // Get IP Address
    axios
      .get('https://api.ipify.org?format=json')
      .then(response => {
        setIpAddress(response?.data?.ip || undefined);
      })
      .catch(error => {
        console.log('Error:', error);
      });
  }, []);

  useEffect(() => {
    if (formRef.current && form) {
      enableHoneypot(formRef.current);
    }
    window?.dataLayer?.push({ event: 'hubspot_form_ready' });
  }, [formRef, form]);

  const validationRef = useRef(validation);
  validationRef.current = validation;

  const getHubspotRedirect = () => {
    if (form.redirect) {
      const { to, href } = parseUrl(form.redirect);
      const redirectUrl = to || href;

      return redirectUrl;
    }

    return null;
  };

  const isFieldInvalid = (value: any, name: string) => {
    const isRequired = findField(form, name).required;

    if (!isRequired) {
      return false;
    }
    if (name === 'email') {
      return !(value && validateEmail(value));
    }

    if (name.includes('phone')) {
      return value?.length !== 14;
    }

    return value === undefined || value === null || value === '';
  };

  const handleChange = (e: SyntheticEvent) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const handleBlur = (e: SyntheticEvent) => {
    setValues({ ...values, [e.target.name]: e.target.value });
    setValidation({ ...validationRef.current, [e.target.name]: isFieldInvalid(e.target.value, e.target.name) });
  };

  const handleAllFieldsValidation = () => {
    const newValidation = Object.keys(values).reduce(
      (accumulator, key) => {
        const field = findField(form, key);
        accumulator[key] = field.required && isFieldVisible(field, values) ? isFieldInvalid(values[key], key) : false;

        return accumulator;
      },
      { ...validation },
    );

    const isFormValid = Object.keys(newValidation).every(key => !newValidation[key]);

    setValidation(newValidation);
    setValidatedOnce(!hasValidatedOnce);

    return isFormValid;
  };

  const getLeadStatus = () => {
    const {
      are_you_located_in_the_united_states,
      form_type,
      fr_industry,
      has_license_radio_select,
      primary_services_lawn_care,
      product_interest,
    }: any = values || {};

    if (form_type !== 'Demo' && form_type !== 'Drift') {
      return 'MGL';
    }
    if (product_interest) {
      return product_interest
        .split(';')
        .some((product: string) => ['Marketing Pro', 'E-Commerce', 'Fleet Pro'].includes(product))
        ? 'MQL'
        : 'MGL';
    }
    if (fr_industry === 'Landscaping') {
      return 'MGL';
    }
    if (fr_industry === 'Lawn Care' && primary_services_lawn_care) {
      return primary_services_lawn_care.includes('Chemical Application') ? 'MQL' : 'MGL';
    }
    if (fr_industry === 'Pest Control') {
      return are_you_located_in_the_united_states === 'true' && has_license_radio_select === 'true' ? 'MQL' : 'MGL';
    }

    // Other & Cleaning
    return are_you_located_in_the_united_states === 'true' ? 'MQL' : 'MGL';
  };

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    if (!handleAllFieldsValidation()) {
      setIsSubmitting(false);

      return;
    }

    const honeypot = formRef?.current?.querySelector('[name="honeypot"]');
    if (honeypot?.value) {
      setIsSubmitting(false);

      return;
    }

    // Setup form values for HubSpot submission
    const fieldsData: { name: string; value: any }[] = prepareFormSubmissionValues(form, values);

    // Add hidden field values from Local Storage
    setGTMData(fieldsData);

    const hutk = getUserHutk();
    const postData = {
      fields: fieldsData,
      context: {
        ...(hutk ? { hutk } : {}),
        ...(ipAddress ? { ipAddress } : {}),
        pageUri: document.baseURI,
        pageName: document.title,
      },
    };

    const formValues = fieldsData?.reduce(
      (acc, cur) => ({ ...acc, [cur.name]: acc[cur.name] ? [acc[cur.name], cur.value].join(', ') : cur.value }),
      {},
    );

    const leadStatus = getLeadStatus();

    window?.dataLayer?.push({
      event: 'hubspot_form_submit',
      analytics: {
        lead_status: leadStatus,
        formdata: {
          ...formValues,
          form_id: hsId,
        },
      },
    });

    try {
      const url = `https://api.hsforms.com/submissions/v3/integration/submit/3381425/${hsId}`;
      axios
        .post(url, postData, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then(() => {
          setFormSubmission(true);

          if (isDownloadPDF && assetUrl) {
            localStorage.setItem('downLoadPDF', 'true');
            navigate(assetUrl);
          }

          const redirect = getHubspotRedirect();

          if (fastlaneId) {
            handleDriftFastlane(fastlaneId, formValues, redirect);

            return;
          }

          if (!fastlaneId && redirect) {
            navigate(redirect);
          }

          onSuccess?.(postData);
        })
        .catch((err: any) => console.log(err));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const downloadFormSubmitted = localStorage.getItem('downLoadPDF');
    if (downloadFormSubmitted && isDownloadPDF) {
      setPDFSubmitted(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        closeModal && closeModal();
        setFormSubmission(false);
        setValidatedOnce(false);
      }
    });
  }, []);

  return (
    <Flex
      flexDirection="column"
      paddingY={40}
      paddingX={32}
      gap={heading ? '32px' : 'unset'}
      boxShadow="xl"
      border="1px solid"
      borderColor="gray-200"
      borderRadius="20px"
      backgroundColor="common-white"
      width="100%"
      marginX="auto"
      height="fit-content"
      alignItems="center"
      minHeight={pdfSubmitted ? 'fit-content' : '306px'}
      className="form-container"
    >
      <Flex flexDirection="column" textAlign="center" gap="12px">
        {heading && (
          <Heading as="h5" textColor="gray-900" fontFamily="secondaryFont" fontSize="h3">
            {heading}
          </Heading>
        )}
        {!formSubmission && body && (
          <Text textStyle="textMd" textColor="gray-700">
            {body?.raw ? richTextParser(body) : body}
          </Text>
        )}
        {formSubmission && form.inlineMessage && (
          <Text
            textStyle="textMd"
            textColor="gray-700"
            width="100%"
            textAlign="center"
            dangerouslySetInnerHTML={{ __html: form.inlineMessage }}
          />
        )}
      </Flex>
      {pdfSubmitted ? (
        <ComponentButton
          label={buttonLabel || 'Download PDF'}
          link={assetUrl}
          handleClick={() => closeModal && closeModal()}
        />
      ) : (
        !formSubmission &&
        form && (
          <Flex width="100%" id={`hubspotForm-${hsId}`} flexDirection="column">
            <form ref={formRef} data-form_type={values?.form_type} noValidate>
              {form.formFieldGroups.map((fieldGroup, index) => (
                <>
                  <HubspotFormFields
                    key={`hs-field-${index}`}
                    fieldGroup={fieldGroup.fields}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    values={values}
                    validation={validation}
                  />
                  {fieldGroup?.fields?.map(field =>
                    field?.dependentFieldFilters?.map((x, index2: number) => (
                      <HubspotFormFields
                        key={`hs-field-${index2}`}
                        fieldGroup={[
                          {
                            ...x.dependentFormField,
                            filters: { ...x.filters, key: field.name },
                          },
                        ]}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        values={values}
                        validation={validation}
                      />
                    )),
                  )}
                </>
              ))}
              <Submit handleSubmit={handleSubmit} label={buttonLabel || form.submitText} isSubmitting={isSubmitting} />
              <PrivacyPolicy privacyPolicyHTML={privacyPolicyHTML} />
            </form>
          </Flex>
        )
      )}
    </Flex>
  );
};

export default HubspotForm;
